import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Button,
    Card,
    CardHeader,
    CardContent,
    FormControl,
    Grid,
    Typography,
    Alert,
    CircularProgress,
    Stack,
    Divider,
    MenuItem,
    Chip
} from '@mui/material';
import {
    Save as SaveIcon,
    Cancel as CancelIcon,
    Timeline as TimelineIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../../hooks/useApiHandler';
import { activityService } from '../../../services/activityService';

const ActivityForm = ({ activity, onSuccess, onCancel, viewMode = false }) => {
    const { handleApiCallWithLoading, showError } = useApiHandler();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        activity_name: '',
        activity_status: 1
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (activity) {
            setFormData({
                activity_name: activity.activity_name || activity.name || '',
                activity_status: activity.activity_status || activity.status || 1
            });
        }
    }, [activity]);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.activity_name.trim()) {
            newErrors.activity_name = 'Activity name is required';
        } else if (formData.activity_name.length > 500) {
            newErrors.activity_name = 'Activity name must be less than 500 characters';
        }

        if (formData.activity_status === undefined || formData.activity_status === null) {
            newErrors.activity_status = 'Status is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const activityData = {
                activity_name: formData.activity_name.trim(),
                activity_status: parseInt(formData.activity_status)
            };

            if (activity) {
                await activityService.updateActivity(activity.id || activity.activity_id, activityData);
            } else {
                await activityService.createActivity(activityData);
            }

            onSuccess();
        } catch (error) {
            console.error('Error during operation:', error);
            showError('Operation failed. Please try again.');
            
            // Handle validation errors
            if (error.errors) {
                const newErrors = {};
                Object.keys(error.errors).forEach(field => {
                    newErrors[field] = error.errors[field][0]; // Take first error message
                });
                setErrors(newErrors);
            }
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        return status === 1 ? 'success' : 'default';
    };

    const getStatusText = (status) => {
        return status === 1 ? 'Active' : 'Inactive';
    };

    return (
        <Card>
            <CardHeader
                avatar={<TimelineIcon color="primary" />}
                title={
                    viewMode 
                        ? 'Activity Details' 
                        : activity 
                            ? 'Edit Activity' 
                            : 'Create New Activity'
                }
                subheader={
                    viewMode 
                        ? 'View activity information'
                        : activity 
                            ? 'Update activity information'
                            : 'Add a new tracking activity to the system'
                }
            />
            <Divider />
            <CardContent>
                <Grid container spacing={3}>
                    {/* Activity Name */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Activity Name"
                            value={formData.activity_name}
                            onChange={(e) => handleInputChange('activity_name', e.target.value)}
                            error={!!errors.activity_name}
                            helperText={errors.activity_name || "Enter a descriptive name for the tracking activity"}
                            disabled={viewMode || loading}
                            required
                            multiline
                            rows={2}
                            placeholder="e.g., Shipment Booked, Out for Delivery, Delivered Successfully"
                        />
                    </Grid>

                    {/* Status */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            select
                            fullWidth
                            label="Status"
                            value={formData.activity_status}
                            onChange={(e) => handleInputChange('activity_status', parseInt(e.target.value))}
                            error={!!errors.activity_status}
                            helperText={errors.activity_status || "Set activity availability status"}
                            disabled={viewMode || loading}
                            required
                        >
                            <MenuItem value={1}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip label="Active" color="success" size="small" />
                                    <Typography variant="body2">Available for use</Typography>
                                </Box>
                            </MenuItem>
                            <MenuItem value={0}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip label="Inactive" color="default" size="small" />
                                    <Typography variant="body2">Not available for use</Typography>
                                </Box>
                            </MenuItem>
                        </TextField>
                    </Grid>

                    {/* Usage Information (View Mode) */}
                    {viewMode && activity && (
                        <>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Usage Count"
                                    value={activity.usage_count || 0}
                                    disabled
                                    helperText="Number of times this activity has been used in tracking"
                                />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Created Date"
                                    value={activity.created_at ? new Date(activity.created_at).toLocaleDateString() : 'N/A'}
                                    disabled
                                    helperText="Date when this activity was created"
                                />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Last Updated"
                                    value={activity.updated_at ? new Date(activity.updated_at).toLocaleDateString() : 'N/A'}
                                    disabled
                                    helperText="Date when this activity was last modified"
                                />
                            </Grid>
                        </>
                    )}

                    {/* Information Alert */}
                    <Grid item xs={12}>
                        <Alert severity="info" icon={<InfoIcon />}>
                            <Typography variant="body2">
                                <strong>Activity Guidelines:</strong>
                                <br />
                                • Use clear, descriptive names that indicate the shipment status
                                <br />
                                • Activities are used in the tracking system to update shipment progress
                                <br />
                                • Inactive activities won't appear in tracking dropdowns
                                <br />
                                • Activities with usage history cannot be deleted
                            </Typography>
                        </Alert>
                    </Grid>
                </Grid>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                    <Button
                        variant="outlined"
                        onClick={onCancel}
                        disabled={loading}
                        startIcon={<CancelIcon />}
                    >
                        {viewMode ? 'Close' : 'Cancel'}
                    </Button>
                    
                    {!viewMode && (
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                        >
                            {loading ? 'Saving...' : (activity ? 'Update Activity' : 'Create Activity')}
                        </Button>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

export default ActivityForm; 